import { createSseParser } from './sse'

// Reads an Artemis reply stream (WEB-7092). Each SSE event carries one JSON object whose
// PascalCase "Type" names it. Every parsed event goes to onEvent, including types this build
// doesn't know, so callers decide what to ignore.
//
// Resolves with the "done" event. Rejects with an ArtemisStreamError when the server sends
// "error" (code from its Code field) or when the body ends with neither ("incomplete").
// Aborting the fetch's signal rejects with the browser's AbortError, which callers treat as
// "stopped" rather than a failure.

export class ArtemisStreamError extends Error {
    constructor(message, code) {
        super(message)
        this.name = 'ArtemisStreamError'
        this.code = code
    }
}

export const isAbortError = err => err?.name === 'AbortError'

export const readArtemisStream = async (response, onEvent = () => {}) => {
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let terminal = null

    const parser = createSseParser(({ data }) => {
        if (terminal) return
        let event
        try { event = JSON.parse(data) } catch (e) { return }
        if (!event || typeof event.Type !== 'string') return

        if (event.Type === 'done' || event.Type === 'error') terminal = event
        onEvent(event)
    })

    try {
        while (!terminal) {
            const { done, value } = await reader.read()
            if (done) {
                parser.feed(decoder.decode())
                parser.end()
                break
            }
            parser.feed(decoder.decode(value, { stream: true }))
        }
    } finally {
        if (terminal) reader.cancel().catch(() => {})
    }

    if (!terminal) throw new ArtemisStreamError('The reply stopped before it finished.', 'incomplete')
    if (terminal.Type === 'error') throw new ArtemisStreamError(terminal.Message || 'Artemis could not answer.', terminal.Code || 'internal_error')
    return terminal
}
